import { type ReactNode } from 'react';

import { type Dampening, useDampenedVisibility } from '../../hooks';
import { mergeSx } from '../../util/mui/sx';

import { LoadingView, type LoadingViewProps } from './LoadingView';

export interface LoadingProps extends LoadingViewProps {
  /**
   * Defines whether or not pointer events should be enabled for the underlying content when
   * rendering the {@link LoadingView} as an overlay.  When explicitly provided as 'true', the
   * {@link LoadingView} will allow pointer events to pass through to the underlying children
   * content, effectively acting as a transparent overlay.
   *
   * This prop can be particularly useful in cases where the loading state is appearing over the
   * child content and the child content should remain scrollable while loading is occurring.
   *
   * This prop is only applicable if 'isOverlay' is 'true'.
   *
   * @default false
   */
  readonly areUnderlyingPointerEventsEnabled?: boolean;
  readonly children?: ReactNode;
  readonly dampening?: Dampening;
  readonly isLoading: boolean;
  /**
   * Defines whether or not the {@link LoadingView} should be rendered as an overlay, appearing on
   * top of the child content instead of replacing it.  When this prop is not explicitly 'true',
   * the {@link LoadingView}, which contains the loading indicator, will fill the entire area of the
   * containing element, replacing any child content inside of the {@link Loading} component.
   *
   * @default false
   */
  readonly isOverlay?: boolean;
}

export const Loading = ({
  areUnderlyingPointerEventsEnabled = false,
  children,
  dampening,
  isLoading = false,
  isOverlay = false,
  ...props
}: LoadingProps) => {
  const dampenedIsLoading = useDampenedVisibility(isLoading, dampening);

  if (dampenedIsLoading) {
    if (isOverlay === false) {
      return <LoadingView {...props} />;
    }
    return (
      <>
        <LoadingView
          zIndex={100}
          {...props}
          sx={mergeSx(
            areUnderlyingPointerEventsEnabled ? { pointerEvents: 'none' } : undefined,
            props.sx,
          )}
        />
        {children}
      </>
    );
  }
  return <>{children}</>;
};
